import { useEffect } from 'react';
import { VoiceIdentification } from '@utils/types/organization/voice-profile.ts';
import { isHumanCorrectedSpeakerName } from '@utils/editors/voice-sample-ranges.ts';

/** Voices that have already fired auto-apply in this tab.
 *
 *  Keyed on (document, label, suggestion) — NOT the row's startMs. Only one
 *  paragraph per voice label auto-applies the suggested name; after that the
 *  backend's override_set flag flips to true and every other paragraph of
 *  that voice sees the stored decision via its chip ("Use Sharon") and can
 *  be renamed per-paragraph. This is the segment-scope mental model: the
 *  automatic path never renames multiple paragraphs behind the user's back,
 *  and each paragraph the user wants to carry the name takes one click.
 *  Cleared on tab reload. */
const AUTO_APPLIED = new Set<string>();

/** Testing hook: reset the coordination set between runs. */
export const resetVoiceAutoApplyState = () => AUTO_APPLIED.clear();

export interface UseVoiceAutoApplyOptions {
  documentId: string;
  speakerLabel: string | null;
  startMs: number;
  identification: VoiceIdentification | undefined;
  currentName: string;
  canReviewSuggestions: boolean;
  canAssign: boolean;
  /** Which scope the auto-apply uses. Editor rows pass `'segment'` so a
   *  mis-diarized file doesn't get renamed wholesale; the Speakers panel
   *  passes `'voice'` so backend identity is committed and a reopen
   *  shows the recognized name without another click. */
  scope: 'voice' | 'segment';
  resolveSpeakerName: (speakerId: number) => string | undefined;
  applyPerson: (
    speakerId: number,
    name: string,
    options: {
      silent: true;
      offerEnrollment: false;
      scope: 'voice' | 'segment';
    }
  ) => Promise<boolean>;
}

/** Apply a strong recognition match without a click.
 *
 * The ticket is explicit that a sufficiently strong match should be shown
 * automatically. The backend's calibrated thresholds decide what counts as
 * strong, so any `suggested_speaker_id` reaching this hook already passed
 * them — the UI just applies it, with four guards:
 *
 *   1. the surface accepts recognition suggestions (not a shared link / read-only),
 *   2. no human decision exists for this label yet,
 *   3. the current display name has not been corrected by a human, and
 *   4. this paragraph has not already auto-applied,
 *
 * so the automatic path never overwrites an explicit human correction and —
 * crucially — only touches the row it ran on. Mis-diarized files that
 * grouped different voices under one label are rename-correctable per row. */
export const useVoiceAutoApply = ({
  documentId,
  speakerLabel,
  startMs,
  identification,
  currentName,
  canReviewSuggestions,
  canAssign,
  scope,
  resolveSpeakerName,
  applyPerson
}: UseVoiceAutoApplyOptions): void => {
  useEffect(() => {
    if (!canReviewSuggestions || !canAssign) return;
    if (!speakerLabel || !identification) return;
    if (identification.override_set) return;
    const suggestedId = identification.suggested_speaker_id;
    if (suggestedId == null) return;
    if (isHumanCorrectedSpeakerName(currentName, speakerLabel)) return;
    const name = resolveSpeakerName(suggestedId);
    if (!name) return;
    const key = `${documentId}:${speakerLabel}:${suggestedId}`;
    if (AUTO_APPLIED.has(key)) return;
    AUTO_APPLIED.add(key);
    void applyPerson(suggestedId, name, {
      silent: true,
      offerEnrollment: false,
      scope
    });
  }, [
    canReviewSuggestions,
    canAssign,
    speakerLabel,
    startMs,
    identification,
    currentName,
    documentId,
    scope,
    resolveSpeakerName,
    applyPerson
  ]);
};
